import isPropValid from '@emotion/is-prop-valid';
import styled from '@emotion/styled';
import type { ReactNode } from 'react';

import { colors, mq, spacing } from '@/lib/theme';

import { Text } from './Text';

export interface DetailsListItem {
	label: string;
	value: ReactNode;
	/** Take the full width of the list, for long values like addresses. */
	span?: boolean;
	/** Set the value large and bold: for the total, the one figure the guest came to check. */
	emphasis?: boolean;
}

interface DetailsListProps {
	items: DetailsListItem[];
	/** Lay the rows out in two columns from the small breakpoint. */
	columns?: 1 | 2;
	/** Draw a rule between rows. Single column only. */
	divided?: boolean;
	className?: string;
}

const layoutProps = new Set(['columns', 'divided', 'span']);
const shouldForwardProp = (prop: string) =>
	isPropValid(prop) && !layoutProps.has(prop);

const List = styled('dl', { shouldForwardProp })<{
	columns: 1 | 2;
	divided: boolean;
}>`
	display: grid;
	grid-template-columns: minmax(0, 1fr);
	gap: ${({ divided }) => (divided ? 0 : `${spacing.space40} ${spacing.space50}`)};
	margin: 0;

	${({ columns }) =>
		columns === 2 &&
		`${mq.sm} {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}`}
`;

const Row = styled('div', { shouldForwardProp })<{
	divided: boolean;
	span?: boolean;
}>`
	display: flex;
	flex-direction: column;
	gap: ${spacing.space10};
	min-width: 0;
	overflow-wrap: anywhere;
	${({ span }) => span && 'grid-column: 1 / -1;'}

	${({ divided }) =>
		divided &&
		`padding: ${spacing.space30} 0;
		border-top: 1px solid ${colors.interface.border};

		&:first-of-type {
			padding-top: 0;
			border-top: 0;
		}
		&:last-of-type {
			padding-bottom: 0;
		}`}
`;

/**
 * Label-over-value rows for the facts of a booking: reference, dates,
 * guests, total. Rendered as a description list so screen readers pair each
 * label with its value.
 */
export const DetailsList = ({
	items,
	columns = 1,
	divided = false,
	className,
}: DetailsListProps) => (
	<List columns={columns} divided={divided} className={className}>
		{items.map((item) => (
			<Row key={item.label} divided={divided} span={item.span}>
				<Text as="dt" variant="label" color="muted">
					{item.label}
				</Text>
				<Text
					as="dd"
					variant={item.emphasis ? 'lead' : 'body'}
					weight={item.emphasis ? 'bold' : undefined}
					color="heading"
				>
					{item.value}
				</Text>
			</Row>
		))}
	</List>
);
